import type { AudienceInput, GroupMembersPage, OwnerRef, UpdateGroupRequest } from '@ralph/contracts'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiRequestError } from '@/api/client.ts'
import {
  addGroupMembers,
  appointGroupManager,
  createGroup,
  deleteGroup,
  fetchGroup,
  fetchGroupDeletionPreview,
  fetchGroupMembers,
  fetchGroups,
  fetchReach,
  type GroupListQuery,
  type ReachItem,
  removeGroupManager,
  removeGroupMember,
  resolvePeople,
  searchPeople,
  updateGroup
} from '@/api/groups.ts'

/**
 * Groups (PRD-26), as queries.
 *
 * One rule decides what every write invalidates: anything that changes who is in a group, who
 * manages it, or what it is called changes three things a reader may be looking at at once — the
 * group's page, every list and picker that shows it, and the signed-in person's own session
 * (their groups and managed groups ride on it, and the shell's navigation hangs off it). A surface
 * where the page updates and the picker does not is one that gets distrusted exactly when it
 * matters.
 */

export const groupKeys = {
  all: ['groups'] as const,
  list: (query: GroupListQuery) => ['groups', 'list', query] as const,
  detail: (id: string) => ['groups', 'detail', id] as const,
  members: (id: string, q: string) => ['groups', 'members', id, q] as const,
  preview: (id: string) => ['groups', 'deletion-preview', id] as const,
  reach: (audience: AudienceInput, item: ReachItem | undefined) => ['groups', 'reach', audience, item] as const,
  people: (q: string) => ['people-search', q] as const
}

export const useGroups = (query: GroupListQuery = {}, enabled = true) =>
  useQuery({
    queryKey: groupKeys.list(query),
    queryFn: () => fetchGroups(query),
    enabled,
    staleTime: 30_000,
    // A search box types into this; the previous list stays on screen until the next arrives
    // rather than flashing empty between keystrokes.
    placeholderData: keepPreviousData
  })

export const useGroup = (id: string | null) =>
  useQuery({
    queryKey: groupKeys.detail(id ?? ''),
    queryFn: () => fetchGroup(id ?? ''),
    enabled: id !== null,
    retry: false
  })

/** Insiders only; the server says `forbidden` to everyone else, and the page does not ask. */
export const useGroupMembers = (id: string | null, q: string, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: groupKeys.members(id ?? '', q),
    queryFn: ({ pageParam }: { pageParam: string | null }) => fetchGroupMembers(id ?? '', { q, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: GroupMembersPage) => last.nextCursor,
    enabled: enabled && id !== null,
    placeholderData: keepPreviousData
  })

export const useGroupDeletionPreview = (id: string | null) =>
  useQuery({
    queryKey: groupKeys.preview(id ?? ''),
    queryFn: () => fetchGroupDeletionPreview(id ?? ''),
    enabled: id !== null,
    staleTime: 0,
    retry: false
  })

/**
 * How many people an audience would reach, and how many the item reaches now — the picker's
 * reach line. Keyed on the audience itself, so ticking a group and unticking it again answers
 * from cache instead of asking twice.
 */
export const useReach = (audience: AudienceInput | null, item?: ReachItem) =>
  useQuery({
    queryKey: groupKeys.reach(audience ?? { kind: 'private', groupIds: [] }, item),
    queryFn: () => fetchReach(audience ?? { kind: 'private', groupIds: [] }, item),
    enabled: audience !== null,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false
  })

/** Managers and administrators only; two characters before it asks (the server's own floor). */
export const usePeopleSearch = (q: string, enabled: boolean) =>
  useQuery({
    queryKey: groupKeys.people(q.trim().toLowerCase()),
    queryFn: () => searchPeople(q),
    enabled: enabled && q.trim().length >= 2,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false
  })

const useGroupInvalidation = () => {
  const client = useQueryClient()
  return (groupId?: string) => {
    void client.invalidateQueries({ queryKey: ['groups', 'list'] })
    void client.invalidateQueries({ queryKey: ['groups', 'reach'] })
    if (groupId) {
      void client.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
      void client.invalidateQueries({ queryKey: ['groups', 'members', groupId] })
      void client.invalidateQueries({ queryKey: groupKeys.preview(groupId) })
    }
    // The reader's own groups ride on the session, and the People page shows everyone's.
    void client.invalidateQueries({ queryKey: ['session'] })
    void client.invalidateQueries({ queryKey: ['people'] })
    // Being in a group is using what is shared with it (a manager may add themselves, D-3): the
    // Knowledge lists, the palette's bases and the gallery answer differently from the next read.
    void client.invalidateQueries({ queryKey: ['knowledge-bases'] })
    void client.invalidateQueries({ queryKey: ['marketplace'] })
  }
}

export const useCreateGroup = () => {
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: createGroup,
    onSuccess: (group) => invalidate(group.id)
  })
}

export const useUpdateGroup = () => {
  const invalidate = useGroupInvalidation()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, version, ...patch }: UpdateGroupRequest & { id: string; version: number }) =>
      updateGroup(id, patch, version),
    onSuccess: (group) => invalidate(group.id),
    // Somebody else's change landed first: read the group again, so the dialog reopens on their
    // version (and a second Save is judged against it) instead of the cached one, forever stale.
    onError: (error, variables) => {
      if (error instanceof ApiRequestError && error.status === 409) {
        void client.invalidateQueries({ queryKey: groupKeys.detail(variables.id) })
        void client.invalidateQueries({ queryKey: ['groups', 'list'] })
      }
    }
  })
}

export const useAddGroupMembers = (groupId: string) => {
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: (userIds: string[]) => addGroupMembers(groupId, userIds),
    onSuccess: () => invalidate(groupId)
  })
}

/**
 * Adding people to a group chosen at the time — from a person's profile, where the person is fixed
 * and the group is what gets picked, so the group cannot be bound when the hook is created.
 */
export const useAddToGroup = () => {
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: string[] }) => addGroupMembers(groupId, userIds),
    onSuccess: (_result, variables) => invalidate(variables.groupId)
  })
}

export const useRemoveGroupMember = (groupId: string) => {
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: (userId: string) => removeGroupMember(groupId, userId),
    onSuccess: () => invalidate(groupId)
  })
}

export const useAppointManager = (groupId: string) => {
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: (userId: string) => appointGroupManager(groupId, userId),
    onSuccess: () => invalidate(groupId)
  })
}

export const useRemoveManager = (groupId: string) => {
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: (userId: string) => removeGroupManager(groupId, userId),
    onSuccess: () => invalidate(groupId)
  })
}

/** Deleting a group moves and narrows things everywhere, so it invalidates everything. */
export const useDeleteGroup = (groupId: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { confirmName: string; digest: string; newOwner?: OwnerRef }) => deleteGroup(groupId, input),
    onSuccess: () => {
      void client.invalidateQueries()
    }
  })
}

export const useResolvePeople = () => useMutation({ mutationFn: resolvePeople })
